// src/pages/calidad/BpmCaptura.jsx
// Captura de incidencias BPM.
// Ajustes de la revisión con Calidad (Fernanda):
//  - Se puede capturar de hoy y hasta 7 días atrás (antes solo hoy o ayer).
//  - Si la persona no aparece en el listado de Asistencias, se agrega a mano
//    (nombre y área) y queda guardada para las siguientes capturas.
//  - Buscador para encontrar rápido a la persona.
import { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';

const DIAS_ATRAS = 7;

const pad = (n) => String(n).padStart(2, '0');
// Fecha LOCAL (toISOString usa UTC y después de las 6 pm ya da el día siguiente)
const fechaLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function opcionesFecha() {
  const hoy = new Date();
  return Array.from({ length: DIAS_ATRAS + 1 }, (_, i) => {
    const d = new Date(hoy);
    d.setDate(hoy.getDate() - i);
    const dia = d.toLocaleDateString('es-MX', { weekday: 'long', day: '2-digit', month: 'short' });
    const etiqueta = i === 0 ? `Hoy · ${dia}` : i === 1 ? `Ayer · ${dia}` : dia.charAt(0).toUpperCase() + dia.slice(1);
    return { valor: fechaLocal(d), etiqueta };
  });
}

/** Junta personas de Asistencias y agregadas a mano en un solo formato */
function normalizarPersonas(asistencias = [], manuales = []) {
  const deAsistencias = asistencias.map((p) => ({
    empleado_id: p.empleado_id,
    nombre: String(p.nombre_completo || p.nombre || '').trim(),
    area: p.area || '',
    retardo: p.estatus_asistencia === 'Retardo',
    manual: false,
  }));
  const deManuales = manuales.map((p) => ({ empleado_id: p.empleado_id, nombre: p.nombre, area: p.area, retardo: false, manual: true }));
  return [...deAsistencias, ...deManuales].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
}

export default function BpmCaptura() {
  const fechas = useMemo(opcionesFecha, []);
  const [fecha, setFecha] = useState(fechas[0].valor);
  const [turno, setTurno] = useState('dia');
  const [personas, setPersonas] = useState([]);
  const [avisoAsistencias, setAvisoAsistencias] = useState(null);
  const [incidencias, setIncidencias] = useState([]);
  const [cargandoPersonas, setCargandoPersonas] = useState(false);
  const [recargar, setRecargar] = useState(0);

  const [busqueda, setBusqueda] = useState('');
  const [empleadoId, setEmpleadoId] = useState('');
  const [incidenciaId, setIncidenciaId] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  // Alta manual
  const [mostrarAlta, setMostrarAlta] = useState(false);
  const [nuevaPersona, setNuevaPersona] = useState({ nombre: '', area: '' });
  const [guardandoPersona, setGuardandoPersona] = useState(false);

  useEffect(() => {
    api.get('/api/calidad/bpm-incidencias').then((res) => {
      setIncidencias(res.data.data.filter((i) => i.activo));
    });
  }, []);

  useEffect(() => {
    async function cargarPersonas() {
      setCargandoPersonas(true);
      try {
        const res = await api.get('/api/calidad/bpm-capturas/personas', { params: { fecha, turno } });
        setPersonas(normalizarPersonas(res.data.data, res.data.manuales));
        setAvisoAsistencias(res.data.aviso || null);
      } catch (err) {
        console.error(err);
        setPersonas([]);
        setAvisoAsistencias('No se pudo cargar el listado de personas.');
      } finally {
        setCargandoPersonas(false);
      }
    }
    cargarPersonas();
  }, [fecha, turno, recargar]);

  // Al cambiar fecha o turno, la persona seleccionada podría no estar; se limpia
  useEffect(() => { setEmpleadoId(''); }, [fecha, turno]);

  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return personas;
    return personas.filter((p) => p.nombre.toLowerCase().includes(q) || p.area.toLowerCase().includes(q));
  }, [personas, busqueda]);

  const areasConocidas = useMemo(
    () => [...new Set([...personas.map((p) => p.area).filter(Boolean), 'Sanidad'])].sort((a, b) => a.localeCompare(b, 'es')),
    [personas]
  );

  const persona = personas.find((p) => String(p.empleado_id) === String(empleadoId));
  const incidencia = incidencias.find((i) => String(i.id) === String(incidenciaId));
  const hayDeAsistencias = personas.some((p) => !p.manual);

  async function handleAgregarPersona() {
    const nombre = nuevaPersona.nombre.trim();
    const area = nuevaPersona.area.trim();
    if (nombre.length < 3 || !area) {
      setMensaje({ tipo: 'error', texto: 'Escribe el nombre completo y el área de la persona.' });
      return;
    }
    try {
      setGuardandoPersona(true);
      const res = await api.post('/api/calidad/bpm-capturas/personas-manuales', { nombre, area });
      const nueva = res.data.data;
      setPersonas((prev) => [
        ...prev.filter((p) => p.empleado_id !== nueva.empleado_id),
        { empleado_id: nueva.empleado_id, nombre: nueva.nombre, area: nueva.area, retardo: false, manual: true },
      ].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')));
      setEmpleadoId(String(nueva.empleado_id));
      setBusqueda('');
      setNuevaPersona({ nombre: '', area: '' });
      setMostrarAlta(false);
      setMensaje({ tipo: 'ok', texto: `${nueva.nombre} (${nueva.area}) agregada. Ya puedes capturarle la incidencia.` });
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'No se pudo agregar a la persona.' });
    } finally {
      setGuardandoPersona(false);
    }
  }

  async function handleCapturar() {
    if (!persona || !incidencia) {
      setMensaje({ tipo: 'error', texto: 'Selecciona la persona y la incidencia.' });
      return;
    }
    try {
      setGuardando(true);
      setMensaje(null);
      const res = await api.post('/api/calidad/bpm-capturas', {
        fecha,
        turno,
        empleado_id: persona.empleado_id,
        empleado_nombre: persona.nombre,
        area: persona.area,
        bpm_incidencia_id: incidencia.id,
        observaciones: observaciones || null,
      });
      const { suspensiones_nuevas, saldo_actual_trimestre } = res.data.data;
      let texto = `✓ Capturado: ${persona.nombre} — ${incidencia.incidencia}.`;
      if (suspensiones_nuevas > 0) {
        texto += ` ⚠ Esta persona llegó a suspensión (${suspensiones_nuevas === 1 ? '1 nueva' : `${suspensiones_nuevas} nuevas`}). Saldo restante del trimestre: ${saldo_actual_trimestre} pts.`;
      } else {
        texto += ` Saldo del trimestre: ${saldo_actual_trimestre} pts.`;
      }
      setMensaje({ tipo: suspensiones_nuevas > 0 ? 'alerta' : 'ok', texto });
      setIncidenciaId('');
      setObservaciones('');
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'No se pudo capturar la incidencia.' });
    } finally {
      setGuardando(false);
    }
  }

  const colorMensaje = { ok: '#3E7C5A', alerta: '#B2662A', error: '#B23A48' };

  return (
    <div style={styles.contenedor} className="contenedor-captura">
      <h1 style={styles.titulo}>Captura de Incidencias BPM</h1>
      <p style={styles.subtitulo}>Puedes capturar de hoy y hasta {DIAS_ATRAS} días atrás.</p>

      <div style={styles.tarjeta}>
        <div style={styles.fila2}>
          <div style={styles.grupo}>
            <label style={styles.label}>Fecha</label>
            <select value={fecha} onChange={(e) => setFecha(e.target.value)} style={styles.input}>
              {fechas.map((f) => <option key={f.valor} value={f.valor}>{f.etiqueta}</option>)}
            </select>
          </div>
          <div style={styles.grupo}>
            <label style={styles.label}>Turno</label>
            <select value={turno} onChange={(e) => setTurno(e.target.value)} style={styles.input}>
              <option value="dia">Día</option>
              <option value="noche">Noche</option>
            </select>
          </div>
        </div>

        <div style={styles.grupo}>
          <label style={styles.label}>Persona {cargandoPersonas && '(cargando...)'}</label>
          <input
            type="text"
            placeholder="Buscar por nombre o área…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            style={styles.input}
            disabled={cargandoPersonas}
          />
          <select value={empleadoId} onChange={(e) => setEmpleadoId(e.target.value)} style={styles.input} disabled={cargandoPersonas}>
            <option value="">— Selecciona {filtradas.length !== personas.length ? `(${filtradas.length} coinciden)` : ''} —</option>
            {filtradas.map((p) => (
              <option key={p.empleado_id} value={p.empleado_id}>
                {p.nombre} — {p.area}{p.retardo ? ' (retardo)' : ''}{p.manual ? ' · agregada a mano' : ''}
              </option>
            ))}
          </select>
          {avisoAsistencias && <span style={styles.avisoVacio}>{avisoAsistencias}</span>}
          {!cargandoPersonas && !avisoAsistencias && !hayDeAsistencias && (
            <span style={styles.avisoVacio}>Nadie checó en Asistencias ese día y turno. Puedes elegir a alguien agregado a mano o agregarlo.</span>
          )}
          {persona?.manual && <span style={styles.ayuda}>Esta persona no está en Asistencias; se agregó a mano.</span>}

          {!mostrarAlta ? (
            <button style={styles.linkBoton} onClick={() => { setMostrarAlta(true); setNuevaPersona({ nombre: busqueda.trim(), area: '' }); }}>
              ¿No aparece en la lista? Agregar persona
            </button>
          ) : (
            <div style={styles.cajaAlta}>
              <div style={{ ...styles.label, marginBottom: 4 }}>Agregar persona que no aparece en Asistencias</div>
              <input
                type="text"
                placeholder="Nombre completo"
                value={nuevaPersona.nombre}
                onChange={(e) => setNuevaPersona({ ...nuevaPersona, nombre: e.target.value })}
                style={styles.input}
                maxLength={150}
              />
              <input
                type="text"
                placeholder="Área (ej. Sanidad)"
                list="bpm-areas"
                value={nuevaPersona.area}
                onChange={(e) => setNuevaPersona({ ...nuevaPersona, area: e.target.value })}
                style={styles.input}
                maxLength={100}
              />
              <datalist id="bpm-areas">{areasConocidas.map((a) => <option key={a} value={a} />)}</datalist>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handleAgregarPersona} disabled={guardandoPersona} style={styles.botonChico}>
                  {guardandoPersona ? 'Guardando...' : 'Agregar y seleccionar'}
                </button>
                <button onClick={() => setMostrarAlta(false)} style={styles.linkBoton}>Cancelar</button>
              </div>
              <span style={styles.ayuda}>Queda guardada para las siguientes capturas. Si ya existe con ese nombre, se usa la misma.</span>
            </div>
          )}
        </div>

        <div style={styles.grupo}>
          <label style={styles.label}>Incidencia</label>
          <select value={incidenciaId} onChange={(e) => setIncidenciaId(e.target.value)} style={styles.input}>
            <option value="">— Selecciona —</option>
            {incidencias.map((i) => (
              <option key={i.id} value={i.id}>
                {i.incidencia} ({i.nivel_riesgo}, {i.puntuacion} pts)
              </option>
            ))}
          </select>
        </div>

        <div style={styles.grupo}>
          <label style={styles.label}>Observaciones (opcional)</label>
          <textarea value={observaciones} onChange={(e) => setObservaciones(e.target.value)} style={{ ...styles.input, height: '70px', resize: 'vertical' }} />
        </div>

        <button onClick={handleCapturar} disabled={guardando} style={styles.botonCapturar}>
          {guardando ? 'Guardando...' : 'Capturar'}
        </button>
        {mensaje && <div style={{ ...styles.mensaje, color: colorMensaje[mensaje.tipo], marginBottom: 0 }}>{mensaje.texto}</div>}
        <button style={{ ...styles.linkBoton, alignSelf: 'flex-start' }} onClick={() => setRecargar((n) => n + 1)}>Actualizar listado de personas</button>
      </div>
    </div>
  );
}

const styles = {
  contenedor: { fontFamily: 'Inter, sans-serif', padding: '24px', color: '#3A2E42', maxWidth: '560px' },
  titulo: { fontFamily: 'Playfair Display, serif', color: '#2A1830', marginBottom: '4px' },
  subtitulo: { fontSize: '12px', color: '#8A7690', marginBottom: '18px' },
  mensaje: { fontSize: '13px', marginBottom: '14px', fontWeight: 600 },
  tarjeta: { background: '#fff', border: '1px solid #E6DDD2', borderRadius: '12px', padding: '18px', boxShadow: '0 2px 14px rgba(74,30,92,0.07)', display: 'flex', flexDirection: 'column', gap: '14px' },
  fila2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  grupo: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '12px', fontWeight: 600, color: '#6E4F80' },
  input: { padding: '8px 10px', fontSize: '13px', border: '1px solid #D6A644', borderRadius: '6px', fontFamily: 'Inter, sans-serif', background: '#FFFDF7', width: '100%', boxSizing: 'border-box' },
  avisoVacio: { fontSize: '11px', color: '#B23A48' },
  ayuda: { fontSize: '11px', color: '#8A7690' },
  linkBoton: { background: 'none', border: 'none', padding: 0, fontSize: '12px', color: '#6A2C75', textDecoration: 'underline', cursor: 'pointer', textAlign: 'left' },
  cajaAlta: { display: 'flex', flexDirection: 'column', gap: '8px', background: '#FAF6F0', border: '1px dashed #D6A644', borderRadius: '8px', padding: '12px' },
  botonChico: { padding: '7px 14px', fontSize: '12px', fontWeight: 600, color: '#fff', background: '#6A2C75', border: 'none', borderRadius: '6px', cursor: 'pointer' },
  botonCapturar: { padding: '10px 14px', fontSize: '13px', fontWeight: 600, color: '#fff', background: '#6A2C75', border: 'none', borderRadius: '8px', cursor: 'pointer' },
};